import axios from 'axios';
import {API_URL} from './api.config';
import {createAuthenticatedApiClient} from './apiClient';
import {
    clearActiveTenantId,
    decodeJwtPayload,
    DEFAULT_CLINIC_ID,
    getTenantIdFromUser,
    getTenantNameFromUser,
    mergeUserWithJwtTenant,
    normalizeAvailableClinics,
    normalizeTenantId,
    setActiveTenantId
} from '@utils/tenant';

const TOKEN_KEY = 'auth_token';
const USER_KEY = 'auth_user';
const AVAILABLE_CLINICS_KEY = 'available_clinics';
const AVAILABLE_EMPRESAS_KEY = 'available_empresas';
const SUPERADMIN_BACKUP_TOKEN_KEY = 'superadmin_backup_token';
const SUPERADMIN_BACKUP_USER_KEY = 'superadmin_backup_user';
const SUPERADMIN_BACKUP_CLINICS_KEY = 'superadmin_backup_clinics';

const ADMIN_EMPRESA_CLIENTE_ROLE = 'ADMIN_EMPRESA_CLIENTE';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const normalizeEmpresaId = value => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const normalized = String(value).trim();

    if (!normalized) {
        return null;
    }

    if (UUID_RE.test(normalized)) {
        return normalized.toLowerCase();
    }

    if (/^\d+$/.test(normalized)) {
        return normalized;
    }

    return null;
};

const normalizeAvailableEmpresas = empresas => {
    if (!Array.isArray(empresas)) {
        return [];
    }

    return empresas
        .map(item => {
            const source = extractEmpresaRecord(item);

            if (!source) {
                return null;
            }

            const id = normalizeEmpresaId(
                source.id ?? source.empresa_id ?? source.empresaId
            );

            if (!id) {
                return null;
            }

            return {
                id,
                nombre:
                    String(source.nombre ?? source.razon_social ?? '').trim() ||
                    `Empresa ${id.slice(0, 8)}`,
                razon_social: source.razon_social != null ? String(source.razon_social) : null,
                rfc: source.rfc != null ? String(source.rfc) : null,
                clinic_id:
                    normalizeTenantId(
                        source.clinic_id ??
                            source.clinicId ??
                            source.tenant_id ??
                            source.tenantId
                    ) ?? null,
                portal_activo: source.portal_activo !== false,
                activa: source.activa !== false && source.activo !== false,
                role_in_empresa:
                    source.role_in_empresa != null ? String(source.role_in_empresa) : null
            };
        })
        .filter(Boolean);
};

const getEmpresaIdFromJwt = token => {
    const payload = decodeJwtPayload(token);
    return normalizeEmpresaId(payload?.empresa_id);
};

const getEmpresaIdFromUser = user => normalizeEmpresaId(user?.empresa_id);

const resolvePortalRole = user =>
    String(user?.role_global ?? user?.role_name ?? user?.role ?? '').trim();

const isAdminEmpresaClienteUser = user =>
    resolvePortalRole(user) === ADMIN_EMPRESA_CLIENTE_ROLE;

const parseEmpresaIdForApi = value => {
    const normalized = normalizeEmpresaId(value);

    if (!normalized) {
        return null;
    }

    if (/^\d+$/.test(normalized)) {
        return Number(normalized);
    }

    return normalized;
};

const extractEmpresaRecord = item => {
    if (!item || typeof item !== 'object') {
        return null;
    }

    if (item.empresa && typeof item.empresa === 'object') {
        return {
            ...item.empresa,
            clinic_id: item.clinic_id ?? item.tenant_id ?? item.empresa.clinic_id ?? item.empresa.tenant_id
        };
    }

    return item;
};

const authAPI = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

export const authService = {
    async signup({nombre, apellidos, email, password, telefono, sexo, clinicId}) {
        const normalizedClinicId = normalizeTenantId(clinicId) ?? DEFAULT_CLINIC_ID;
        const body = {
            nombre,
            apellidos,
            email,
            password,
            telefono,
            sexo,
            clinic_id: normalizedClinicId
        };

        const {data} = await authAPI.post('/auth/signup', body, {
            params: {clinic: normalizedClinicId}
        });
        return data;
    },

    async confirmEmail(token) {
        const {data} = await authAPI.post('/auth/confirm-email', {token});
        return data;
    },

    async login(email, password) {
        const {data} = await authAPI.post('/auth/login', {email, password});
        return this._persistLoginSession(data);
    },

    async startDemo() {
        const {data} = await authAPI.post('/auth/demo', {});
        return this._persistLoginSession({...data, demo: true});
    },

    async endDemo() {
        try {
            if (this.isAuthenticated()) {
                await createAuthenticatedApiClient().post('/auth/demo/end', {});
            }
        } catch {
            // Igual limpiamos sesión local
        }
        this.logout();
    },

    isDemoSession(token = this.getToken()) {
        if (!token) {
            return false;
        }
        const payload = decodeJwtPayload(token);
        if (payload?.demo) {
            return true;
        }
        return Boolean(this.getUser()?.is_demo);
    },

    _persistLoginSession(data) {
        const token = data.token || data.accessToken;
        const user = data.user || null;
        const availableClinics = normalizeAvailableClinics(data.available_clinics);
        const availableEmpresas = normalizeAvailableEmpresas(data.available_empresas);

        if (token) {
            localStorage.setItem(TOKEN_KEY, token);
        }

        clearActiveTenantId();
        this.setAvailableClinics(availableClinics);
        this.setAvailableEmpresas(availableEmpresas);

        if (user) {
            const mergedUser = mergeUserWithJwtTenant({
                ...user,
                is_demo: Boolean(data.demo || user.is_demo),
                available_clinics: availableClinics,
                available_empresas: availableEmpresas,
                empresa_id:
                    getEmpresaIdFromJwt(token) ??
                    getEmpresaIdFromUser(user) ??
                    normalizeEmpresaId(data.empresa_id) ??
                    (availableEmpresas.length === 1 ? normalizeEmpresaId(availableEmpresas[0]?.id) : null)
            }, token);
            const storedUser = this.setStoredUser(mergedUser);

            if (isAdminEmpresaClienteUser(storedUser) && !this.hasCompletePortalContext(token)) {
                clearActiveTenantId();
            } else {
                const tenantId = getTenantIdFromUser(storedUser);

                if (tenantId) {
                    setActiveTenantId(tenantId);
                }
            }
        }

        window.dispatchEvent(new CustomEvent('auth-session-changed'));

        return data;
    },

    async selectClinic(clinicId) {
        const normalizedClinicId = normalizeTenantId(clinicId);

        if (!normalizedClinicId) {
            throw new Error('Clínica inválida');
        }

        const {data} = await createAuthenticatedApiClient().post('/auth/select-clinic', {
            clinic_id: normalizedClinicId
        });
        const token = data.token || data.accessToken;
        const user = data.user || this.getUser();
        const availableClinics = normalizeAvailableClinics(
            data.available_clinics ?? this.getAvailableClinics()
        );

        if (token) {
            localStorage.setItem(TOKEN_KEY, token);
        }

        this.setAvailableClinics(availableClinics);

        const mergedUser = mergeUserWithJwtTenant({
            ...user,
            available_clinics: availableClinics
        }, token, {
            tenant_id: data.tenant_id,
            tenant_name: data.tenant_name,
            role_global: data.role_global,
            role_in_clinic: data.role_in_clinic,
            clinic: data.clinic ?? data.user?.clinic
        });
        const storedUser = this.setStoredUser(mergedUser);
        const tenantId = getTenantIdFromUser(storedUser);

        if (tenantId) {
            setActiveTenantId(tenantId);
        } else {
            clearActiveTenantId();
        }

        window.dispatchEvent(new CustomEvent('auth-session-changed'));

        return data;
    },

    hasCompletePortalContext(token = this.getToken()) {
        const payload = decodeJwtPayload(token);

        return Boolean(
            normalizeTenantId(payload?.tenant_id) && normalizeEmpresaId(payload?.empresa_id)
        );
    },

    needsPortalEmpresaPicker(user = this.getUser(), token = this.getToken()) {
        if (!isAdminEmpresaClienteUser(user)) {
            return false;
        }

        if (this.hasCompletePortalContext(token)) {
            return false;
        }

        return this.getAvailableEmpresas().length > 1;
    },

    buildSelectEmpresaBody(empresaRef) {
        if (empresaRef === undefined || empresaRef === null) {
            return {};
        }

        if (typeof empresaRef === 'object' && !Array.isArray(empresaRef)) {
            const source = extractEmpresaRecord(empresaRef);
            const body = {};

            const empresaId = parseEmpresaIdForApi(
                source?.id ?? source?.empresa_id ?? source?.empresaId ?? empresaRef.empresa_id
            );

            const clinicId = normalizeTenantId(
                source?.clinic_id ??
                    source?.clinicId ??
                    source?.tenant_id ??
                    source?.tenantId ??
                    empresaRef.clinic_id ??
                    empresaRef.tenant_id
            );

            if (empresaId != null) {
                body.empresa_id = empresaId;
            }

            if (clinicId) {
                body.clinic_id = clinicId;
            }

            return body;
        }

        const normalizedEmpresaId = normalizeEmpresaId(empresaRef);

        if (!normalizedEmpresaId) {
            throw new Error('Empresa inválida');
        }

        const match = this.getAvailableEmpresas().find(
            empresa => String(empresa.id) === String(normalizedEmpresaId)
        );

        if (match) {
            const body = {};

            const empresaId = parseEmpresaIdForApi(match.id);

            if (empresaId != null) {
                body.empresa_id = empresaId;
            }

            if (match.clinic_id) {
                body.clinic_id = match.clinic_id;
            }

            return body;
        }

        const empresaId = parseEmpresaIdForApi(normalizedEmpresaId);

        return empresaId != null ? {empresa_id: empresaId} : {};
    },

    resolveAvailableEmpresasForSelection(loginData) {
        const fromLogin = normalizeAvailableEmpresas(loginData?.available_empresas);

        if (fromLogin.length) {
            return fromLogin;
        }

        return this.getAvailableEmpresas();
    },

    async selectEmpresa(empresaRef) {
        let body = this.buildSelectEmpresaBody(empresaRef);

        if (!body.empresa_id) {
            const empresas = this.getAvailableEmpresas();

            if (empresas.length === 1) {
                body = this.buildSelectEmpresaBody(empresas[0]);
            }
        }

        const {data} = await createAuthenticatedApiClient({skipTenantHeader: true}).post(
            '/auth/select-empresa',
            body
        );
        const token = data.token || data.accessToken;
        const user = data.user || this.getUser();
        const availableEmpresas = normalizeAvailableEmpresas(
            data.available_empresas ?? this.getAvailableEmpresas()
        );

        if (token) {
            localStorage.setItem(TOKEN_KEY, token);
        }

        this.setAvailableEmpresas(availableEmpresas);

        const selectedEmpresaId =
            normalizeEmpresaId(data.empresa_id) ??
            normalizeEmpresaId(body.empresa_id) ??
            getEmpresaIdFromJwt(token);

        const mergedUser = mergeUserWithJwtTenant({
            ...user,
            available_empresas: availableEmpresas,
            empresa_id: selectedEmpresaId,
            empresa_nombre: data.empresa_nombre ?? data.empresa?.nombre ?? user?.empresa_nombre ?? null
        }, token, {
            tenant_id: data.tenant_id,
            tenant_name: data.tenant_name,
            role_global: data.role_global ?? ADMIN_EMPRESA_CLIENTE_ROLE
        });
        const storedUser = this.setStoredUser(mergedUser);
        const tenantId = getTenantIdFromUser(storedUser) ?? normalizeTenantId(data.tenant_id);

        if (tenantId) {
            setActiveTenantId(tenantId);
        } else {
            clearActiveTenantId();
        }

        window.dispatchEvent(new CustomEvent('auth-session-changed'));

        return data;
    },

    async fetchAuthSession(options = {}) {
        const {includeInactive = false} = options;
        const params = includeInactive ? {include_inactive: true} : undefined;
        const user = this.getUser();
        const skipTenantHeader =
            isAdminEmpresaClienteUser(user) || this.isImpersonationViewActive();
        const {data} = await createAuthenticatedApiClient({skipTenantHeader}).get('/auth/me', {
            params
        });
        return data;
    },

    getImpersonationClaims(token = this.getToken()) {
        const payload = decodeJwtPayload(token);

        if (!payload || typeof payload !== 'object') {
            return null;
        }

        const mode = payload.impersonation_mode;

        return {
            impersonated_by: payload.impersonated_by ? String(payload.impersonated_by).trim() : null,
            impersonation_mode: mode === 'A' || mode === 'B' ? mode : null,
            empresa_id: normalizeEmpresaId(payload.empresa_id),
            tenant_id: normalizeTenantId(payload.tenant_id),
            role_global: payload.role_global ? String(payload.role_global).trim() : null
        };
    },

    /**
     * Sesión de vista del portal cliente iniciada por SuperAdmin (Modo A).
     * No debe mezclarse con el panel administrativo.
     */
    isImpersonationViewActive(token = this.getToken()) {
        const claims = this.getImpersonationClaims(token);

        if (!claims) {
            return false;
        }

        if (claims.impersonated_by) {
            if (claims.impersonation_mode === 'B') {
                return false;
            }

            return Boolean(claims.empresa_id);
        }

        if (this.hasImpersonationBackup() && claims.empresa_id) {
            return true;
        }

        return false;
    },

    shouldUseClientPortalShell(token = this.getToken(), user = this.getUser()) {
        if (this.isImpersonationViewActive(token)) {
            return true;
        }

        return isAdminEmpresaClienteUser(user);
    },

    applyImpersonationSessionFromJwt(token = this.getToken()) {
        const claims = this.getImpersonationClaims(token);
        const currentUser = this.getUser();

        if (!token || !claims?.empresa_id) {
            return currentUser;
        }

        const mergedUser = mergeUserWithJwtTenant({
            ...currentUser,
            role_global: claims.role_global || ADMIN_EMPRESA_CLIENTE_ROLE,
            empresa_id: claims.empresa_id,
            empresa_nombre: currentUser?.empresa_nombre ?? null
        }, token, {
            tenant_id: claims.tenant_id,
            role_global: claims.role_global || ADMIN_EMPRESA_CLIENTE_ROLE
        });
        const storedUser = this.setStoredUser(mergedUser);

        if (claims.tenant_id) {
            setActiveTenantId(claims.tenant_id);
        }

        window.dispatchEvent(new CustomEvent('auth-session-changed'));

        return storedUser;
    },

    async hydrateSessionFromToken(options = {}) {
        const {skipApi = false} = options;
        const token = this.getToken();

        if (!token) {
            return null;
        }

        const currentUser = this.getUser();
        const jwtMergedUser = mergeUserWithJwtTenant(currentUser, token);

        if (jwtMergedUser) {
            this.setStoredUser(jwtMergedUser);

            const jwtTenantId = getTenantIdFromUser(jwtMergedUser);

            if (jwtTenantId) {
                setActiveTenantId(jwtTenantId);
            }
        }

        if (skipApi || this.isImpersonationViewActive(token)) {
            const user = this.applyImpersonationSessionFromJwt(token);

            return {
                user,
                fromApi: false,
                impersonation: true
            };
        }

        try {
            const sessionData = await this.fetchAuthSession(options);
            const apiUser = sessionData?.user ?? {};
            const session = sessionData?.session ?? {};
            const availableClinics = normalizeAvailableClinics(sessionData?.available_clinics);
            const availableEmpresas = normalizeAvailableEmpresas(sessionData?.available_empresas);
            const activeToken = this.getToken();

            this.setAvailableClinics(availableClinics);
            this.setAvailableEmpresas(availableEmpresas);

            const mergedUser = mergeUserWithJwtTenant({
                ...this.getUser(),
                ...apiUser,
                available_clinics: availableClinics,
                available_empresas: availableEmpresas,
                empresa_id: getEmpresaIdFromJwt(activeToken) ?? getEmpresaIdFromUser(apiUser)
            }, activeToken, {
                tenant_id: session.tenant_id ?? apiUser.tenant_id,
                tenant_name: session.tenant_name ?? apiUser.tenant_name,
                role_global: session.role_global ?? apiUser.role_global,
                role_in_clinic: apiUser.role_in_clinic,
                clinic: apiUser.clinic
            });
            const storedUser = this.setStoredUser(mergedUser);
            const tenantId = getTenantIdFromUser(storedUser) ?? normalizeTenantId(session.tenant_id);

            if (tenantId) {
                setActiveTenantId(tenantId);
            } else if (!session.has_tenant_selected) {
                clearActiveTenantId();
            }

            if (
                isAdminEmpresaClienteUser(storedUser) &&
                session.has_empresa_selected === false
            ) {
                await this.ensureEmpresaContextSelected({
                    user: storedUser,
                    available_empresas: availableEmpresas
                });
            }

            window.dispatchEvent(new CustomEvent('auth-session-changed'));

            return {
                ...sessionData,
                user: this.getUser()
            };
        } catch (error) {
            window.dispatchEvent(new CustomEvent('auth-session-changed'));
            return {
                user: this.getUser(),
                fromApi: false,
                error
            };
        }
    },

    getPostLoginPath(data, token = this.getToken()) {
        const resolvedToken = token || data?.token || data?.accessToken;
        const user = mergeUserWithJwtTenant(data?.user ?? this.getUser(), resolvedToken) ?? data?.user ?? this.getUser();

        if (isAdminEmpresaClienteUser(user)) {
            if (this.hasCompletePortalContext(resolvedToken)) {
                return '/portal-cliente/dashboard';
            }

            if (this.needsPortalEmpresaPicker(user, resolvedToken)) {
                return '/login?portal_step=empresa';
            }

            return '/login?reason=empresa-context';
        }

        const jwtPayload = decodeJwtPayload(resolvedToken);
        const tenantId = normalizeTenantId(jwtPayload?.tenant_id) ?? getTenantIdFromUser(user);

        if (!tenantId) {
            return '/select-clinic';
        }

        return '/dashboard_a';
    },

    async ensureEmpresaContextSelected(loginData) {
        const user = loginData?.user ?? this.getUser();
        const token = loginData?.token || loginData?.accessToken || this.getToken();

        if (!isAdminEmpresaClienteUser(user) && !this.isImpersonationViewActive(token)) {
            return this.getEmpresaId(user, token);
        }

        if (this.hasCompletePortalContext(token)) {
            return this.getEmpresaId(user, token);
        }

        const empresas = this.resolveAvailableEmpresasForSelection(loginData);

        if (empresas.length > 1) {
            return null;
        }

        try {
            if (empresas.length === 1) {
                await this.selectEmpresa(empresas[0]);
            } else {
                await this.selectEmpresa();
            }

            return this.hasCompletePortalContext() ? this.getEmpresaId() : null;
        } catch (error) {
            console.error('No se pudo auto-seleccionar la empresa vinculada:', error);
            return null;
        }
    },

    async resolvePostLoginNavigation(loginData) {
        const token = loginData?.token || loginData?.accessToken || this.getToken();
        const user = loginData?.user ?? this.getUser();
        const portalUser = mergeUserWithJwtTenant(user, token) ?? user;

        if (isAdminEmpresaClienteUser(portalUser)) {
            const empresaId = await this.ensureEmpresaContextSelected(loginData);

            if (empresaId && this.hasCompletePortalContext()) {
                return '/portal-cliente/dashboard';
            }

            if (this.needsPortalEmpresaPicker(portalUser, token)) {
                return '/login?portal_step=empresa';
            }

            return '/login?reason=empresa-context';
        }

        const path = this.getPostLoginPath(loginData, token);

        if (path !== '/select-clinic') {
            return path;
        }

        const clinics = this.getAvailableClinics();

        if (clinics.length === 1) {
            await this.selectClinic(clinics[0].id);
            return '/dashboard_a';
        }

        return '/select-clinic';
    },

    setAvailableClinics(clinics) {
        const normalized = normalizeAvailableClinics(clinics);

        if (!normalized.length) {
            localStorage.removeItem(AVAILABLE_CLINICS_KEY);
            return [];
        }

        localStorage.setItem(AVAILABLE_CLINICS_KEY, JSON.stringify(normalized));
        return normalized;
    },

    getAvailableClinics() {
        const raw = localStorage.getItem(AVAILABLE_CLINICS_KEY);

        if (!raw) {
            const user = this.getUser();
            return normalizeAvailableClinics(user?.available_clinics);
        }

        try {
            return normalizeAvailableClinics(JSON.parse(raw));
        } catch {
            return [];
        }
    },

    clearAvailableClinics() {
        localStorage.removeItem(AVAILABLE_CLINICS_KEY);
    },

    setAvailableEmpresas(empresas) {
        const normalized = normalizeAvailableEmpresas(empresas);

        if (!normalized.length) {
            localStorage.removeItem(AVAILABLE_EMPRESAS_KEY);
            return [];
        }

        localStorage.setItem(AVAILABLE_EMPRESAS_KEY, JSON.stringify(normalized));
        return normalized;
    },

    getAvailableEmpresas() {
        const raw = localStorage.getItem(AVAILABLE_EMPRESAS_KEY);

        if (!raw) {
            const user = this.getUser();
            return normalizeAvailableEmpresas(user?.available_empresas);
        }

        try {
            return normalizeAvailableEmpresas(JSON.parse(raw));
        } catch {
            return [];
        }
    },

    clearAvailableEmpresas() {
        localStorage.removeItem(AVAILABLE_EMPRESAS_KEY);
    },

    isAdminEmpresaCliente(user = this.getUser()) {
        return isAdminEmpresaClienteUser(user);
    },

    getEmpresaId(user = this.getUser(), token = this.getToken()) {
        return getEmpresaIdFromUser(user) ?? getEmpresaIdFromJwt(token);
    },

    async requestPasswordReset(email) {
        const {data} = await authAPI.post('/auth/request-password-reset', {email});
        return data;
    },

    async resetPassword(token, newPassword) {
        const {data} = await authAPI.post('/auth/reset-password', {token, newPassword});
        return data;
    },

    async getInvitationInfo(token) {
        const {data} = await authAPI.get('/auth/invitation-info', {
            params: {token}
        });
        return data?.data || data;
    },

    async registerComplete({token, password, telefono, sexo, apellidos}) {
        const body = {token, password};

        if (telefono?.trim()) {
            body.telefono = telefono.trim();
        }

        if (sexo) {
            body.sexo = sexo;
        }

        if (apellidos?.trim()) {
            body.apellidos = apellidos.trim();
        }

        const {data} = await authAPI.post('/auth/register-complete', body);
        return data;
    },

    backupSessionForImpersonation() {
        if (localStorage.getItem(SUPERADMIN_BACKUP_TOKEN_KEY)) {
            return;
        }

        const token = this.getToken();
        const user = this.getUser();
        const clinics = this.getAvailableClinics();

        if (token) {
            localStorage.setItem(SUPERADMIN_BACKUP_TOKEN_KEY, token);
        }

        if (user) {
            localStorage.setItem(SUPERADMIN_BACKUP_USER_KEY, JSON.stringify(user));
        }

        if (clinics.length) {
            localStorage.setItem(SUPERADMIN_BACKUP_CLINICS_KEY, JSON.stringify(clinics));
        }
    },

    clearImpersonationBackup() {
        localStorage.removeItem(SUPERADMIN_BACKUP_TOKEN_KEY);
        localStorage.removeItem(SUPERADMIN_BACKUP_USER_KEY);
        localStorage.removeItem(SUPERADMIN_BACKUP_CLINICS_KEY);
    },

    hasImpersonationBackup() {
        return Boolean(localStorage.getItem(SUPERADMIN_BACKUP_TOKEN_KEY));
    },

    /**
     * Aplica la respuesta de select-context (parcial o completa).
     * @param {object} data
     * @param {{ isComplete?: boolean }} options
     */
    applySuperAdminContextResponse(data, options = {}) {
        const {isComplete = false} = options;
        this.backupSessionForImpersonation();

        const token = data.token || data.accessToken;
        const availableEmpresas = normalizeAvailableEmpresas(data.available_empresas);

        if (token) {
            localStorage.setItem(TOKEN_KEY, token);
        }

        if (availableEmpresas.length) {
            this.setAvailableEmpresas(availableEmpresas);
        } else if (isComplete) {
            this.clearAvailableEmpresas();
        }

        if (isComplete) {
            clearActiveTenantId();
        }

        const user = data.user || this.getUser();
        const mergedUser = mergeUserWithJwtTenant({
            ...user,
            available_empresas: availableEmpresas.length ? availableEmpresas : this.getAvailableEmpresas(),
            empresa_id: data.empresa_id ?? getEmpresaIdFromJwt(token) ?? getEmpresaIdFromUser(user),
            empresa_nombre: data.empresa_nombre ?? user?.empresa_nombre ?? null
        }, token, {
            tenant_id: data.tenant_id,
            tenant_name: data.tenant_name,
            role_global: isComplete
                ? (data.role_global || ADMIN_EMPRESA_CLIENTE_ROLE)
                : data.role_global
        });
        const storedUser = this.setStoredUser(mergedUser);
        const tenantId = getTenantIdFromUser(storedUser) ?? normalizeTenantId(data.tenant_id);

        if (tenantId) {
            setActiveTenantId(tenantId);
        } else if (isComplete) {
            clearActiveTenantId();
        }

        window.dispatchEvent(new CustomEvent('auth-session-changed'));

        return {
            token,
            user: storedUser
        };
    },

    /** Restaura la sesión de SuperAdmin tras salir del modo vista. */
    async exitImpersonationView() {
        if (this.isImpersonationViewActive() || this.hasImpersonationBackup()) {
            try {
                const {data} = await createAuthenticatedApiClient({skipTenantHeader: true}).post(
                    '/auth/superadmin/exit-impersonation'
                );
                const restoredToken = data?.token || data?.accessToken;

                if (restoredToken) {
                    localStorage.setItem(TOKEN_KEY, restoredToken);

                    if (data?.user) {
                        this.setStoredUser(mergeUserWithJwtTenant(data.user, restoredToken));
                    }

                    this.clearAvailableEmpresas();
                    this.clearImpersonationBackup();

                    const tenantId = normalizeTenantId(data?.tenant_id) ?? getTenantIdFromUser(this.getUser());

                    if (tenantId) {
                        setActiveTenantId(tenantId);
                    } else {
                        clearActiveTenantId();
                    }

                    window.dispatchEvent(new CustomEvent('auth-session-changed'));
                    return {restored: true};
                }
            } catch (error) {
                console.warn('Fallo exit-impersonation API, usando respaldo local:', error);
            }
        }

        const backupToken = localStorage.getItem(SUPERADMIN_BACKUP_TOKEN_KEY);

        if (!backupToken) {
            this.logout();
            return {restored: false};
        }

        localStorage.setItem(TOKEN_KEY, backupToken);

        const backupUserRaw = localStorage.getItem(SUPERADMIN_BACKUP_USER_KEY);

        if (backupUserRaw) {
            try {
                const backupUser = JSON.parse(backupUserRaw);
                this.setStoredUser(mergeUserWithJwtTenant(backupUser, backupToken));
            } catch {
                localStorage.removeItem(USER_KEY);
            }
        }

        const backupClinicsRaw = localStorage.getItem(SUPERADMIN_BACKUP_CLINICS_KEY);

        if (backupClinicsRaw) {
            try {
                this.setAvailableClinics(JSON.parse(backupClinicsRaw));
            } catch {
                this.clearAvailableClinics();
            }
        }

        this.clearAvailableEmpresas();
        this.clearImpersonationBackup();

        const restoredUser = this.getUser();
        const tenantId = getTenantIdFromUser(restoredUser);

        if (tenantId) {
            setActiveTenantId(tenantId);
        } else {
            clearActiveTenantId();
        }

        window.dispatchEvent(new CustomEvent('auth-session-changed'));

        return {restored: true};
    },

    logout() {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        clearActiveTenantId();
        this.clearAvailableClinics();
        this.clearAvailableEmpresas();
        this.clearImpersonationBackup();
        window.dispatchEvent(new CustomEvent('auth-session-changed'));
    },

    isAuthenticated() {
        return !!localStorage.getItem(TOKEN_KEY);
    },

    getToken() {
        return localStorage.getItem(TOKEN_KEY);
    },

    getUser() {
        const raw = localStorage.getItem(USER_KEY);
        return raw ? JSON.parse(raw) : null;
    },

    getTenantId() {
        return getTenantIdFromUser(this.getUser());
    },

    /** Guarda el usuario en sesión; `foto_perfil_url` debe ser la ruta relativa del API. */
    setStoredUser(user) {
        if (!user) {
            localStorage.removeItem(USER_KEY);
            return null;
        }

        const normalizedUser = {
            ...user,
            tenant_id: getTenantIdFromUser(user) ?? user.tenant_id ?? null,
            tenant_name: getTenantNameFromUser(user) || user.tenant_name || null
        };

        localStorage.setItem(USER_KEY, JSON.stringify(normalizedUser));
        window.dispatchEvent(new CustomEvent('auth-user-updated', {detail: {user: this.getUser()}}));
        return this.getUser();
    },

    /** Fusiona en auth_user y notifica a la UI (barra lateral, perfil, etc.). */
    updateStoredUser(patch) {
        const raw = localStorage.getItem(USER_KEY);
        const current = raw ? JSON.parse(raw) : {};
        const next = {...current, ...patch};
        return this.setStoredUser(next);
    }
};
